// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import { specificationsStyles } from "@west/Overrides/Widgets/ProductDetails/ProductDetailsSpecifications";
import AnchorCircleUp from "@west/XDL/Icons/AnchorCircleUp";
import AngleDown from "@west/XDL/Icons/AngleDown";
import AngleUp from "@west/XDL/Icons/AngleUp";
import { avoidBreakInsidePrintElement, color, mergeWithCss, xScreen } from "@west/XDL/Utilities";
import { css } from "styled-components";

const accordionSectionStyles: AccordionSectionPresentationProps = {
    mergeCss: false,
    headerProps: {
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding-bottom: 13px;
            margin-bottom: 16px;

            &&& > button {
                &,
                &:focus {
                    outline: none;
                    cursor: initial;
                    padding: 0;
                    background: transparent;
                    border: none;
                    align-items: flex-end;
                }
            }

            @media print {
                & > button {
                    display: flex !important;
                }
            }
        `,
    },
    collapseIconProps: {},
    panelProps: {
        css: css`
            border: 0;
            padding: 0;
            margin: 0 0 32px 0;
            font-size: 14px;
            pre {
                white-space: pre-wrap;
            }

            @media print {
                display: block;
            }
        `,
    },
    toggleIconProps: {
        color: "common.ui4",
        src: AngleUp,
        size: 16,
        css: css`
            cursor: pointer;
            &:hover {
                color: ${color("common.ui9")};
            }
        `,
    },
    expandIconProps: {
        color: "common.ui4",
        src: AngleDown,
    },
    toggleTransition: {
        positionerCss: css`
            margin: auto;
            @media print {
                display: none;
            }
        `,
    },
};

specificationsStyles.attributeAccordionSection = {
    ...mergeWithCss(accordionSectionStyles, {
        panelProps: {
            css: css`
                display: ${({ hidden }: any) => (hidden ? "none" : "grid")};
                grid-template-columns: 1fr 1fr 1fr;
                gap: 8px 22px;

                ${xScreen("lte", "md")} {
                    grid-template-columns: 1fr;
                }
            `,
        },
    } as AccordionSectionPresentationProps),
};

specificationsStyles.videoAccordionSection = {
    ...accordionSectionStyles,
};

specificationsStyles.specificationsAccordionSection = {
    ...mergeWithCss(accordionSectionStyles, {
        panelProps: {
            css: css`
                ul {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                    padding: 0 0 0 20px;
                }

                ${xScreen("gt", "lg")} {
                    ul {
                        display: flex;
                        flex-wrap: wrap;
                        flex-direction: row;
                        gap: 20px 60px;
                        justify-content: space-between;
                        li {
                            flex-basis: calc(50% - 60px);
                        }
                    }
                }
            `,
        },
    } as AccordionSectionPresentationProps),
};

specificationsStyles.titleWrapper = {
    css: css`
        display: flex;
        align-items: flex-end;
        gap: 12px 24px;
        width: calc(100% - 20px);
        flex-wrap: wrap;
    `,
};

specificationsStyles.title = {
    variant: "h2",
    css: css`
        margin-bottom: 0;
        white-space: nowrap;
    `,
};

specificationsStyles.backToTop = {
    typographyProps: {
        size: 12,
    },
    icon: {
        iconProps: {
            src: AnchorCircleUp,
            size: 14,
            css: css`
                opacity: 0.66;
            `,
        },
        position: "right",
    },
    css: css`
        &&& {
            width: auto;
            background: transparent;
            padding: 0;
            border: none;
            margin-bottom: 2px;
        }
    `,
};

specificationsStyles.primaryVideo = {
    centeringWrapper: {
        css: css`
            min-height: 178px;
            height: 178px;
            margin: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            ${avoidBreakInsidePrintElement}
            img {
                height: auto;
                max-width: 100%;
                max-height: 100%;
            }
        `,
    },
};

specificationsStyles.specificationsVideoModal = {
    cssOverrides: {
        modalContainer: css`
            width: auto;
        `,
        modalBody: css`
            width: auto;
            max-width: initial;
            max-height: initial !important;
        `,
        modalTitle: css`
            border-bottom: none;
            padding: 10px 10px 0;
        `,
        modalContent: css`
            text-align: center;
        `,
    },
};

specificationsStyles.attributeTypeWrapper = {
    css: css`
        display: flex;
        align-items: center;
        box-shadow: 0 1px 0 0 ${color("common.ui3")};
        padding: 8px 12px;

        ${xScreen("lte", "md")} {
            padding: 0;
            box-shadow: none;
        }
    `,
};

specificationsStyles.attributeTextWrapper = {
    css: css`
        display: flex;
        & > ${TypographyStyle}:first-child {
            white-space: nowrap;
        }

        ${xScreen("lte", "md")} {
            justify-content: space-between;
            width: 100%;
            & > ${TypographyStyle} {
                flex: 1 0;
                &:first-child {
                    white-space: initial;
                }
            }
        }
    `,
};

specificationsStyles.specificationsAccordion = {
    headingLevel: 2,
    css: css`
        ${avoidBreakInsidePrintElement}
    `,
};
